import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '../i18n/locale-context';
import { countryConfig } from '../country';
import { suggestCompanies, type CompanyEntry, type CompanySuggestion } from '../lib/auth-client';
import { pluralRuIndex } from '../lib/format';
import { useKeyboardSpaceBelow } from '../lib/viewport';

// UZ ИНН = 9 digits, KZ БИН/ИИН = 12 — see `countryConfig.companyIdForm`.
const TIN_DIGITS = countryConfig.companyIdForm.digits;

/** Same thresholds as the API (`company-suggest.ts`): below them it answers
 *  `[]`, so asking would only spend a request. */
const MIN_LETTERS = 3;
const MIN_DIGITS = 3;
const DEBOUNCE_MS = 250;
/** Matches the onboarding niche search's `max-h-72` cap. */
const LIST_CEILING_PX = 288;

/**
 * What people put BETWEEN the digits of a number: any whitespace (`\s` already
 * covers the no-break spaces a phone keyboard or a pasted document brings —
 * U+00A0, U+202F, U+2007), the zero-width characters `\s` does not cover, every
 * dash (`\p{Pd}`: hyphen, non-breaking hyphen, en/em dash) plus the minus sign,
 * and the dot. kaztender.com, Redmi, 2026-10-22: «870 101 300 123».
 *
 * ⚠️ The API strips the same set before it checks the number
 * (`compactCompanyId`, services/api/src/modules/auth/validate-tin.ts) — change
 * both or neither.
 */
const SEPARATOR = String.raw`[\s\u200b-\u200d\u2060\p{Pd}\u2212.]`;
const ID_SEPARATORS = new RegExp(`${SEPARATOR}+`, 'gu');

/** A run of digits with at most ONE separator between any two of them —
 *  «870 101 300 123», «870-101-300-123». A wider gap («12 - 301234567») ends
 *  the run, so a house number never fuses with the id beside it. */
const SPACED_DIGITS = new RegExp(String.raw`\d(?:${SEPARATOR}?\d)*`, 'gu');

/**
 * The ИНН / БИН the field holds, or `''`.
 *
 *  - Digits only, separators allowed («301 234 567», «870 101 300 123») →
 *    those digits, however many. Never TRUNCATED: ten digits stay ten, so the
 *    caller's exact-length gate refuses them — cutting one off would submit a
 *    different company's number.
 *  - A name AND the id in one go («Savdo Group 301234567», «ТОО Ромашка
 *    870 101 300 123»; owner 2026-10-18: «если человек ввёл и то и другое») →
 *    the one number of exactly an id's length, written solid or with
 *    separators. Two different such numbers, or none, → `''`: guessing which is
 *    the id would be the same silent wrong-company risk.
 *  - A name alone → `''`, and the caller's «Продолжить» waits for a pick.
 *
 * Split on non-digits rather than a lookbehind regex: `(?<!\d)` throws at
 * parse time on iOS Safari before 16.4.
 */
export function tinFromLookup(text: string): string {
  const compact = text.replace(ID_SEPARATORS, '');
  if (/^\d+$/.test(compact)) return compact;
  const spaced = (text.match(SPACED_DIGITS) ?? []).map((run) => run.replace(ID_SEPARATORS, ''));
  const solid = text.split(/\D+/);
  const ids = new Set([...spaced, ...solid].filter((run) => run.length === TIN_DIGITS));
  return ids.size === 1 ? [...ids][0] : '';
}

/** True while the field holds a NAME rather than digits — the caller's Enter
 *  must not answer that with «нужно 9 цифр». */
export function isNameLookup(text: string): boolean {
  return /\p{L}/u.test(text);
}

/** What `text` is worth asking the API about — `null` when nothing. */
function lookupQuery(text: string): string | null {
  const q = text.trim();
  if (!q) return null;
  // An ИНН in the field — alone or beside a name — is looked up as an ИНН:
  // it is the part that identifies the company.
  const digits = tinFromLookup(q);
  if (digits) {
    return digits.length >= MIN_DIGITS && digits.length <= TIN_DIGITS ? digits : null;
  }
  if (!isNameLookup(q)) return null;
  return [...q].filter((ch) => /\p{L}/u.test(ch)).length >= MIN_LETTERS ? q : null;
}

/** A company picked from the suggestions, and which kind of query found it. */
export interface CompanyPick {
  company: CompanySuggestion;
  via: CompanyEntry;
}

/**
 * «Введите ИНН или название вашей организации» — ONE field for the company step
 * that takes either (owner, 2026-10-18).
 *
 * On a phone only 25 % of new accounts answered the ИНН step, against 53 % on
 * desktop (30 days to 2026-10-17): the 9 digits are not to hand, the company's
 * name is. So typing letters looks the company up by name and offers up to
 * eight under the field; a tap SELECTS one (its name fills the field, its ИНН
 * is shown under it) and the caller's «Продолжить» submits that ИНН through the
 * same `POST /auth/me/company` as a typed number. Typing digits is the old
 * path, unchanged — the caller reads them through {@link tinFromLookup}.
 *
 * Nothing found is never a dead end: a soft line says so (and, where the
 * caller has a skip, that skipping is fine), «Продолжить» simply stays off and
 * the caller's «Пропустить» stays where it is. No separate manual mode — the
 * same field takes the ИНН (owner, 2026-10-18: two buttons, nothing else).
 *
 * The list is IN the flow, under the field — not an overlay — so neither a
 * dialog's `overflow` nor a bottom sheet can clip it; with a phone keyboard up
 * it is capped to the space left above the keyboard and scrolls inside
 * ({@link useKeyboardSpaceBelow}, the onboarding niche search's pattern).
 */
export function CompanyLookup({
  value,
  onChange,
  picked,
  onPick,
  canSkip = false,
  disabled = false,
  describedBy,
  label,
  emptyText,
  onNameNotFound,
  className = '',
}: {
  /** The field's text — a name or digits. */
  value: string;
  /** The reader typed: the caller must drop any `picked` company — the text no
   *  longer names it. */
  onChange: (text: string) => void;
  /** The selected suggestion, held by the caller (it owns the submit). */
  picked: CompanyPick | null;
  onPick: (pick: CompanyPick) => void;
  /** The caller offers «Пропустить» — the «not found» line may say so. */
  canSkip?: boolean;
  /** A submission is in flight: suggestions stop taking taps. The input stays
   *  enabled — disabling it drops a phone's keyboard mid-answer. */
  disabled?: boolean;
  /** `aria-describedby` for the input — the caller's length hint. */
  describedBy?: string;
  /** The visible label, when the caller asks a different question than the
   *  company step (the ИИ step, 2026-10-19). Defaults to `company.lookup.label`. */
  label?: string;
  /** The «nothing found» line, when nothing found means something else there —
   *  on the ИИ step the name is still used. Defaults to the skip/no-skip lines. */
  emptyText?: string;
  /** Told whether the field holds a NAME the lookup answered with no company — the state
   *  the «nothing found» line describes. The company step builds its «Пропустить» (UZ) /
   *  «Далее», keep the typed name (KZ) button on it (2026-10-21). */
  onNameNotFound?: (notFound: boolean) => void;
  className?: string;
}) {
  const t = useT();
  // The anchor is the INPUT, not the wrapper: the list is in the flow, so the
  // wrapper's bottom edge is the list's own — measuring from it would shrink
  // the list by its own height.
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  // Nothing to look up once a company is picked: the field now shows its name.
  const query = picked ? null : lookupQuery(value);
  const [debounced, setDebounced] = useState<string | null>(null);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const { data, isFetching } = useQuery({
    queryKey: ['company-suggest', debounced],
    queryFn: () => suggestCompanies(debounced ?? ''),
    enabled: debounced !== null,
    staleTime: 60_000,
    // A failed lookup is «no suggestions» — the typed-ИНН path still works,
    // and an error line under a field the reader has not finished typing in
    // would be noise.
    retry: false,
  });

  const nameQuery = query !== null && isNameLookup(query);
  // Rows only for the query on screen: a reply to an older one would offer
  // companies that no longer match what is typed.
  const current = query !== null && debounced === query;
  const rows = current ? (data ?? []) : [];
  const settled = current && !isFetching;
  // Digits with no match say nothing: the ИНН path is right there.
  const listOpen = query !== null && (rows.length > 0 || nameQuery);
  const nameNotFound = nameQuery && settled && rows.length === 0;
  // A LAYOUT effect: the caller's button changes in the same commit as the «nothing found»
  // line, never one frame (or one test assertion) later.
  useLayoutEffect(() => {
    onNameNotFound?.(nameNotFound);
  }, [nameNotFound, onNameNotFound]);
  const listMaxH = useKeyboardSpaceBelow(inputRef, listOpen, { ceiling: LIST_CEILING_PX });

  function winsLine(n: number): string {
    const form = (['one', 'few', 'many'] as const)[pluralRuIndex(n)];
    return t(`company.lookup.wins.${form}`, { n });
  }

  function details(c: CompanySuggestion): string {
    return [
      c.region,
      c.awards24m > 0 ? winsLine(c.awards24m) : null,
      t('company.lookup.tinValue', { value: c.tin }),
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return (
    <div className={className}>
      {/* A VISIBLE label (owner, 2026-10-18: «Введите ИНН или название вашей
          организации»), not only an aria-label — a placeholder disappears on
          the first keystroke, and the field takes two kinds of answer. */}
      <label htmlFor={inputId} className="mb-1.5 block text-[13px] font-semibold text-ink-soft">
        {label ?? t('company.lookup.label')}
      </label>
      <input
        id={inputId}
        ref={inputRef}
        aria-describedby={describedBy}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        maxLength={100}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('company.lookup.placeholder', { digits: TIN_DIGITS })}
        className="h-11 w-full rounded-md border-[1.5px] border-edge bg-bone px-3 text-[16px] text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand"
      />

      {/* The pick, confirmed by the one thing a name cannot: its ИНН. Two
          companies can share a name («"SAVDO" MCHJ» is two registry rows). */}
      {picked && (
        <p className="mt-1.5 text-[12.5px] leading-snug text-ink-soft" aria-live="polite">
          {details(picked.company)}
        </p>
      )}

      {listOpen && (
        <div
          role="listbox"
          style={listMaxH === undefined ? undefined : { maxHeight: listMaxH }}
          className="mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-edge bg-bone-surface"
        >
          {rows.map((c) => (
            <button
              key={c.tin}
              type="button"
              role="option"
              aria-selected={false}
              disabled={disabled}
              onClick={() => onPick({ company: c, via: nameQuery ? 'name' : 'tin' })}
              className="flex w-full flex-col items-start gap-0.5 border-b border-edge px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-bone disabled:opacity-60"
            >
              <span className="line-clamp-2 text-[14px] font-semibold leading-snug text-ink">
                {c.name}
              </span>
              <span className="text-[12px] leading-snug text-ink-muted">{details(c)}</span>
            </button>
          ))}
          {nameQuery && rows.length === 0 && (
            <p className="px-3 py-2.5 text-[13px] text-ink-muted" aria-live="polite">
              {!settled
                ? t('company.lookup.searching')
                : (emptyText ??
                  (canSkip ? t('company.lookup.empty') : t('company.lookup.emptyNoSkip')))}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
